import React, { useState, useCallback, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import BackgroundLayer from './components/BackgroundLayer';
import Header from './components/Header';
import Hero from './components/sections/Hero';
import About from './components/sections/About';
import Programs from './components/sections/Programs';
import AdultKarate from './components/sections/AdultKarate';
import Team from './components/sections/Team';
import Gallery from './components/sections/Gallery';
import History from './components/sections/History';
import Schedule from './components/sections/Schedule';
import MidCTA from './components/sections/MidCTA';
import Contact from './components/sections/Contact';
import Footer from './components/sections/Footer';
import ChatWidget from './components/ChatWidget';
import MobileCTA from './components/MobileCTA';
import ExitIntentModal from './components/ExitIntentModal';
import LeadModal from './components/LeadModal';
import FAQ from './components/sections/FAQ';
import AdminPage from './pages/AdminPage';
import AdultKaratePage from './components/pages/AdultKaratePage';
import KidsKaratePage from './components/pages/KidsKaratePage';
import TeenMartialArtsPage from './components/pages/TeenMartialArtsPage';
import KempoKaratePage from './components/pages/KempoKaratePage';
import SelfDefensePage from './components/pages/SelfDefensePage';
import AboutPage from './components/pages/AboutPage';
import SchedulePage from './components/pages/SchedulePage';
import ContactPage from './components/pages/ContactPage';
import FaqPage from './components/pages/FaqPage';
import { LeadModalContext } from './lib/leadModalContext';
import { usePageMeta } from './lib/useMeta';
import { initAnalytics, trackEvent } from './lib/analytics';
import './App.css';

/* Scroll to top on route change (fallback for Chrome which doesn't scroll on SPA nav). */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      // Let anchor navigation handle itself
      const id = hash.slice(1);
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
        return;
      }
    }
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname, hash]);
  return null;
}

function MarketingSite({ autoOpenLead = false, adSource = null }) {
  const [leadOpen, setLeadOpen] = useState(false);
  const [leadSource, setLeadSource] = useState('website');
  const [leadDefaults, setLeadDefaults] = useState({});

  usePageMeta({
    title: 'Adult Karate in Colorado Springs, CO | Universal Kempo Karate',
    description:
      'Explore adult karate, Kempo, and practical self-defense at Universal Kempo Karate in Colorado Springs. Beginners welcome. Ask about a free trial class.',
    path: '/',
    breadcrumbs: [{ label: 'Home' }],
  });

  const openLeadModal = useCallback((source = 'website', defaults = {}) => {
    setLeadSource(source);
    setLeadDefaults(defaults);
    setLeadOpen(true);
    trackEvent('trial_click', { source });
  }, []);

  // Analytics init
  useEffect(() => {
    initAnalytics();
  }, []);

  // Auto-open lead modal for Google Ads routes (/book-trial, /free-trial)
  useEffect(() => {
    if (autoOpenLead) {
      const t = setTimeout(() => {
        setLeadSource(adSource || 'ad-book-trial');
        setLeadOpen(true);
        trackEvent('ad_landing', { source: adSource || 'book-trial' });
      }, 600);
      return () => clearTimeout(t);
    }
  }, [autoOpenLead, adSource]);

  // Smooth scroll for anchor links on same page
  useEffect(() => {
    const handler = (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const id = a.getAttribute('href').slice(1);
      const el = document.getElementById(id);
      if (el) {
        e.preventDefault();
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  // Track outbound directions click via global capture
  useEffect(() => {
    const handler = (e) => {
      const a = e.target && e.target.closest ? e.target.closest('a') : null;
      if (!a) return;
      const href = a.getAttribute('href') || '';
      if (href.includes('google.com/maps') || href.includes('maps.app.goo.gl')) {
        trackEvent('directions_click', { source: a.getAttribute('data-testid') || 'anonymous-directions' });
      }
    };
    document.addEventListener('click', handler, { capture: true });
    return () => document.removeEventListener('click', handler, { capture: true });
  }, []);

  return (
    <LeadModalContext.Provider value={{ openLeadModal }}>
      <div className="App" data-testid="app-root">
        <BackgroundLayer />
        <Header />
        <main>
          <Hero />
          <About />
          <AdultKarate />
          <Programs />
          <Team />
          <Gallery />
          <History />
          <Schedule />
          <FAQ />
          <MidCTA />
          <Contact />
        </main>
        <Footer />
        <ChatWidget />
        <MobileCTA />
        <ExitIntentModal />
        <LeadModal
          open={leadOpen}
          onOpenChange={setLeadOpen}
          source={leadSource}
          defaults={leadDefaults}
        />
        <Toaster
          theme="dark"
          position="top-center"
          toastOptions={{
            style: {
              background: 'rgba(15,18,23,0.95)',
              color: '#fff',
              border: '1px solid rgba(255,42,42,0.45)',
            },
          }}
        />
      </div>
    </LeadModalContext.Provider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        {/* Admin + internal */}
        <Route path="/admin" element={<AdminPage />} />

        {/* Google Ads landing pages (preserve existing: auto-opens modal) */}
        <Route path="/book-trial" element={<MarketingSite autoOpenLead={true} adSource="ad-book-trial" />} />
        <Route path="/free-trial" element={<MarketingSite autoOpenLead={true} adSource="ad-free-trial" />} />

        {/* Dedicated SEO-focused program & info pages */}
        <Route path="/adult-karate" element={<AdultKaratePage />} />
        <Route path="/kids-karate" element={<KidsKaratePage />} />
        <Route path="/teen-martial-arts" element={<TeenMartialArtsPage />} />
        {/* Preserve legacy ad route /teen-karate - redirect feel via dedicated page */}
        <Route path="/teen-karate" element={<TeenMartialArtsPage />} />
        <Route path="/kempo-karate" element={<KempoKaratePage />} />
        <Route path="/self-defense" element={<SelfDefensePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/schedule" element={<SchedulePage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/faq" element={<FaqPage />} />

        {/* Homepage + unknown routes fall through to homepage */}
        <Route path="/" element={<MarketingSite />} />
        <Route path="*" element={<MarketingSite />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
